// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// MUI
import { Box, Skeleton, Stack, Typography } from "@mui/material";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Layouts
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";

// Components
import RepairOrdersTable from "./RepairOrdersTable";

// Hooks
import { useRepairOrdersQuery } from "./hooks/useRepairOrdersQuery";
import useConfigurationData from "@/Common/hooks/Configuration/useConfigurationData";
import { useRepairOrdersUpdateSubscription } from "./hooks/useRepairOrdersUpdateSubscription";
import { useMappingFilters } from "@Common/hooks/meta/useMappingFilters.ts";

interface FniDetailsLayerProps {
	periodTreeSelection: IPeriodTreeSelection;
	isTabSelected: boolean;
	payPeriodTitle?: string | null;
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	hasPermission: boolean;
}

export function RepairOrdersBody(props: Readonly<FniDetailsLayerProps>) {
	const { isTabSelected, payPeriodTitle, locationId, commissionCycleId, payPeriodId, hasPermission } = props;

	const { configuration, error: loadConfigError, isLoading: isLoadingConfiguration } = useConfigurationData({ locationId });
	const errorLoadingConfig = Boolean(loadConfigError);
	const dmsProvider = configuration?.administrationTools?.integrations?.dmsProvider?.provider ?? "";

	const {
		isLoadingFilters,
		mappingFiltersData,
		error: loadFiltersError,
	} = useMappingFilters({
		enabled: and(isTabSelected, Boolean(dmsProvider), hasPermission),
		provider: dmsProvider,
		category: "RO",
	});
	const errorLoadingFilters = Boolean(loadFiltersError);

	const hasErrorLoadingPreconditions = errorLoadingConfig || errorLoadingFilters;

	const {
		repairOrdersData,
		isPaginating,
		isLoading: isLoadingRepairOrders,
	} = useRepairOrdersQuery({
		locationId,
		commissionCycleId,
		payPeriodId,
		enabled: and(isTabSelected, !hasErrorLoadingPreconditions, hasPermission),
	});

	useRepairOrdersUpdateSubscription({
		locationId,
		commissionCycleId,
		payPeriodId,
	});

	const hasRepairOrdersData = !!repairOrdersData?.length;
	const noDataLoaded = and(!isLoadingConfiguration, !isLoadingFilters, !isLoadingRepairOrders, !hasRepairOrdersData);
	const isLoading = or(isLoadingConfiguration, isLoadingRepairOrders, isLoadingFilters);

	if (isLoading) {
		return (
			<Box height={500} mt={5}>
				<TableLoader toolbar buttons={2} content rows={5} multiTitles={8} />
			</Box>
		);
	}

	return (
		<Stack className="py-8 h-[690px]">
			<Header title={payPeriodTitle} />

			{hasErrorLoadingPreconditions && <ErrorState />}

			{!hasErrorLoadingPreconditions && (
				<>
					{noDataLoaded && <EmptyState />}

					<Stack className="pt-4 !h-full">
						{hasRepairOrdersData && (
							<RepairOrdersTable
								isPaginating={isPaginating}
								items={repairOrdersData}
								mappingFilters={mappingFiltersData ?? []}
							/>
						)}
					</Stack>
				</>
			)}
		</Stack>
	);
}

// MARK:- Supporting Components
function Header(props: { title?: string | null }) {
	const { title } = props;

	if (!title) {
		return <Skeleton variant="rectangular" height={30} width={350} className={"rounded-[6px] mb-[15px]"} />;
	}
	return <Typography className={"page-title"}>{`${title} Repairs Orders`}</Typography>;
}

function ErrorState() {
	return <EmptyBody image={empty} title={"There was an error while loading data"} bodyHeightClassName={"!h-[380px]"} />;
}

function EmptyState() {
	return <EmptyBody image={emptyTable} title={"No Repairs Orders data for this period"} bodyHeightClassName={"!h-[380px]"} />;
}
